<template>
  <div>
    <Head title="Visual Inspections" />
    <h1 class="mb-8 text-3xl font-bold">Visual Inspection</h1>
    <div class="bg-white rounded-md shadow overflow-hidden mb-5">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-2 -mr-6 pt-8 pl-8 pr-8 pb-8">
          <text-input
            v-model="form.quantity"
            :error="form.errors.quantity"
            class="pb-2 pr-6 w-full lg:w-1/2 hidden"
            placeholder="Quantity"
          />
          <text-input
            v-model="form.item_id"
            :error="form.errors.item_id"
            class="pb-2 pr-6 w-full lg:w-full"
            placeholder="Scan UID"
            @keyup="getSkuComponents"
          />
          <p><small style="color:red;">{{ errorMsg }}</small></p>
        </div>
        <hr />
        <div
          v-if="isValid"
          class="flex flex-wrap -mb-2 -mr-6 pt-8 pl-8 pr-8 pb-8"
        >
          <div
            v-for="(component, index) in componentsData"
            :key="component.component_id"
            class="pb-2 pr-6 w-1/2 lg:w-1/2"
          >
            <text-input
              :key="index"
              v-model="component.value"
              :error="form.errors.value"
              class="pb-8 pr-6 w-full lg:w-full"
              :label="component.name"
              :value="component.value"
            />
          </div>
        </div>
        <div
          v-if="isValid"
          class="flex items-center justify-end w-full px-8 py-4 bg-gray-50 border-t border-gray-100"
        >
          <loading-button
            :loading="form.processing"
            class="btn-orange px-3 py-2 text-xs"
            type="submit"
          >
            Add Visual Inspection
          </loading-button>
        </div>
      </form>
    </div>
    <hr />
    <ul
      id="tabs-tabFill" class="nav nav-tabs flex flex-col md:flex-row flex-wrap list-none border-b-0 pl-0 mb-4"
      role="tablist"
    >
      <li class="nav-item flex-auto text-center" role="presentation">
        <a
          id="tabs-home-tabFill" href="#tabs-homeFill" class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent
          active
        " data-bs-toggle="pill" data-bs-target="#tabs-homeFill" role="tab"
          aria-controls="tabs-homeFill" aria-selected="true"
        >Today's Scans</a>
      </li>
      <li class="nav-item flex-auto text-center" role="presentation">
        <a
          id="tabs-profile-tabFill" href="#tabs-profileFill" class="
          nav-link
          w-full
          block
          font-medium
          text-xs
          leading-tight
          uppercase
          border-x-0 border-t-0 border-b-2 border-transparent
          px-6
          py-3
          my-2
          hover:border-transparent hover:bg-gray-100
          focus:border-transparent
        " data-bs-toggle="pill" data-bs-target="#tabs-profileFill" role="tab"
          aria-controls="tabs-profileFill" aria-selected="false"
        >All Scans</a>
      </li>
    </ul>
    <div 
      id="tabs-tabContentFill"
      class="tab-content"
    >
      <div id="tabs-homeFill" class="tab-pane fade show active" role="tabpanel" aria-labelledby="tabs-home-tabFill">
        <div class="bg-white rounded-md shadow overflow-x-auto mt-5">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-3 pt-3 px-3">Date</th>
              <th class="pb-3 pt-3 px-3">UID</th>
              <th class="pb-3 pt-3 px-3">Part Number</th>
              <th class="pb-3 pt-3 px-3">Part Description</th>
              <th class="pb-3 pt-3 px-3 text-center">Component Part Number</th>
              <th class="pb-3 pt-3 px-3 text-center">Component Part Description</th>
              <th class="pb-3 pt-3 px-3">PO Number</th>
              <th class="pb-3 pt-3 px-3">Supplier</th>
              <th class="pb-3 pt-3 px-3">Container Number</th>
              <th class="pb-3 pt-3 px-3" colspan="2">Proportion(%)</th>
            </tr>
            <tr v-for="item in visualInspections.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 focus:text-orange-500">
                  {{ item.date }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.item_uid }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.part_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 text-center" tabindex="-1">
                  {{ item.component_part_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 text-center" tabindex="-1">
                  {{ item.component_part_description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.purchase_order }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.supplier }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.container_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.proportion }}
                </Link>
              </td>
              <td class="w-px border-t">
                <Link class="flex items-center px-3" tabindex="-1">
                  <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
                </Link>
              </td>
            </tr>
            <tr v-if="visualInspections.data.length === 0">
              <td class="px-6 py-4 border-t" colspan="8">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="visualInspections.links" />
      </div>
      <div id="tabs-profileFill" class="tab-pane fade" role="tabpanel" aria-labelledby="tabs-profile-tabFill">
        <div class="flex items-center justify-between mb-6">
          <date-range v-model="search.dateValue" class="mr-0 w-full max-w-md mr-3" @reset="reset" />
          <a
            v-if="dailyInspections.data.length > 0"
            class="btn-orange px-3 py-2 text-xs cursor-pointer"
            :href="`/visual-inspections/download/${search.dateValue}`"
            download
            target="__blank"
          >
            <span>Download</span>
          </a>
        </div>
        <div class="bg-white rounded-md shadow overflow-x-auto mt-5">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-3 pt-3 px-3">Date</th>
              <th class="pb-3 pt-3 px-3">UID</th>
              <th class="pb-3 pt-3 px-3">Part Number</th>
              <th class="pb-3 pt-3 px-3">Part Description</th>
              <th class="pb-3 pt-3 px-3 text-center">Component Part Number</th>
              <th class="pb-3 pt-3 px-3 text-center">Component Part Description</th>
              <th class="pb-3 pt-3 px-3">PO Number</th>
              <th class="pb-3 pt-3 px-3">Supplier</th>
              <th class="pb-3 pt-3 px-3">Container Number</th>
              <th class="pb-3 pt-3 px-3" colspan="2">Proportion(%)</th>
            </tr>
            <tr v-for="item in dailyInspections.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 focus:text-orange-500">
                  {{ item.date }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.item_uid }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.part_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 text-center" tabindex="-1">
                  {{ item.component_part_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2 text-center" tabindex="-1">
                  {{ item.component_part_description }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.purchase_order }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.supplier }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.container_no }}
                </Link>
              </td>
              <td class="border-t">
                <Link class="flex items-center px-3 py-2" tabindex="-1">
                  {{ item.proportion }}
                </Link>
              </td>
              <td class="w-px border-t">
                <Link class="flex items-center px-3" tabindex="-1">
                  <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
                </Link>
              </td>
            </tr>
            <tr v-if="dailyInspections.data.length === 0">
              <td class="px-6 py-4 border-t" colspan="8">No data found</td>
            </tr>
          </table>
        </div>
        <pagination class="mt-6" :links="visualInspections.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
//import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import Pagination from '@/Shared/Pagination'
import DateRange from '@/Shared/DateRange'
import axios from 'axios'

export default {
  components: {
    Head,
    Link,
    DateRange,
    TextInput,
    LoadingButton,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    visualInspections: Object,
    dailyInspections: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        item_id: null,
        quantity: null,
        componentData: {
          id: null,
          component_id: null,
          bale_id: null,
          name: null,
          value: null,
        },
      }),
      isValid: false,
      errorMsg: null,
      baleComponents: null,
      search: {
        dateValue: this.filters.dateValue,
      },
    }
  },
  computed: {
    componentsData() {
      const typesObj = []
      this.baleComponents.forEach((item) => {
        typesObj.push({
          id: item.id,
          bale_id:item.bale_id,
          component_id: item.component_id,
          name: item.description,
          value: 0,
        })
      })
      return typesObj || []
    },
    role() {
      return this.$inertia.page.props.auth.user.role
    },
  },
  watch: {
    search: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/visual-inspections/', pickBy(this.search), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.search = mapValues(this.search, () => null)
    },
    store() {
      this.form.componentData = this.componentsData
      this.form.post('/visual-inspections', {
        onError: () => {
          this.errorMsg = 'Component proportions must amount to 100'
        },
      }) 
      this.form.item_id = ''
      this.form.quantity = ''
      this.baleComponents = null
      this.errorMsg = ''
      this.isValid = false
    },
    getSkuComponents() {
      this.errorMsg = ''
      if (this.form.item_id) {
        axios.get(`/bale-components/${this.form.item_id}/search`).then(response => {
          console.log(response.data)
          if (response.data.message) {
            this.errorMsg = response.data.message
          } else {
            this.isValid = true
            this.baleComponents = response.data.components
          }
        })
      }
    },
  },
}
</script>
